import { select } from 'redux-saga/effects';
import moment from 'moment-timezone';
import { metricKeys } from '@pulse/metrics';
import { createGroupBy, isErrorCodeByDimension, isErrorCodeByMetric } from '@utils/dimension';
import { addPercentageMetrics } from '@utils/metrics';
import birdChartSelector from '@store/common/birdChart/selectors';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { selector as commonSelector } from '@store/common';
import { realTimeModeOnTrendsSelector, currentMetricsMetaSelector } from '@store/common/selectors';
import { METRICS_MAPPING } from '@utils';
import getHourlyIntervalFormat from '@utils/getHourlyIntervalFormat';
import dimensionTablesSelector, {
  metricsSelector as dimensionTablesMetricsSelector,
} from './selectors';
import { query as originQuery } from './schema';
import * as viewerDrillDownBirdChartSelectors from '../trends/birdChart/selectors';

export function* viewerDrillDownInterval(query) {
  const cloneQuery = query;
  const interval = yield select(viewerDrillDownBirdChartSelectors.chartIntervalSelector);
  if (moment().diff(interval.from, 'days') > 31) {
    const { start, end } = getHourlyIntervalFormat(interval.from, interval.to);
    cloneQuery.interval = `${moment(start).format()}/${moment(end).format()}`;
  } else {
    cloneQuery.interval = `${moment(interval.from).format()}/${moment(interval.to).format()}`;
  }
}

function* createQuery() {
  const commonStore = yield select(commonSelector);
  const currentMetricsMeta = yield select(currentMetricsMetaSelector);
  const metricName = yield select(selectedMetricSelector);
  const dimensionTablesStore = yield select(dimensionTablesSelector);
  const metrics = yield select(dimensionTablesMetricsSelector);
  const birdChartStore = yield select(birdChartSelector);

  const { interval } = commonStore;
  const filter = commonStore.appliedFilters[0].rule;
  const { dimensions, dimensionSortByMetric } = dimensionTablesStore;
  const { sliceFrom, sliceTo } = birdChartStore;
  const dimensionNames = Object.keys(dimensions);
  const realTimeMode = yield select(realTimeModeOnTrendsSelector);

  if (
    dimensionNames.length === 0 ||
    !filter ||
    (sliceFrom && sliceTo && !interval.from && !interval.to)
  ) {
    return null;
  }

  const query = {
    ...originQuery,
    filter,
    metrics: [...metrics],
  };
  // metric
  query.metrics = addPercentageMetrics(query.metrics, currentMetricsMeta);

  // interval
  if (sliceFrom && sliceTo) {
    query.interval = `${sliceFrom}/${sliceTo}`;
  } else {
    query.interval = `${interval.from}/${interval.to}`;
  }

  query.isLiveMode = realTimeMode;
  // sortBy
  query.sortBy = [
    {
      metric: dimensionSortByMetric || METRICS_MAPPING[metricName] || metricName,
      order: 'desc',
    },
  ];

  return query;
}

function* createQueryByDimensionName(dimensionName, dimensionList) {
  const query = yield createQuery();
  // groupBy
  const dimension = dimensionList.find((originDimension) => originDimension.name === dimensionName);

  let groupBy = createGroupBy(dimension || dimensionName);
  if (groupBy && groupBy.includes('=')) {
    groupBy = groupBy.split('=').slice(1).join('=');
  }
  query.groupBy = [groupBy];
  // error codes
  if (isErrorCodeByDimension(dimensionName)) {
    query.metrics = query.metrics.filter((metric) => isErrorCodeByMetric(metric));
    if (query.metrics.find((metric) => metric.includes(metricKeys.VideoPlaybackFailures))) {
      query.metrics.unshift(metricKeys.EndedPlays);
    } else {
      query.metrics.unshift(metricKeys.Attempts);
    }
    query.sortBy = [
      {
        metric: query.metrics[query.metrics.length - 1],
        order: 'desc',
      },
    ];
  }

  return query;
}

function* setQueryByDimensionName(queries, dimensionName, dimensionList) {
  const mirrorQueries = queries;
  mirrorQueries[dimensionName] = yield createQueryByDimensionName(dimensionName, dimensionList);
}

export { createQuery, createQueryByDimensionName, setQueryByDimensionName };
